import { PauseFilled, PlayFilledAlt, StopFilledAlt } from '@carbon/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '@/shared/format/number';
import { cx } from '@/shared/lib/cx';
import { Button } from './Button';
import { IconButton } from './IconButton';
import s from './Media.module.css';

export type RecordingStatus = 'recording' | 'paused' | 'uploading' | 'offline' | 'processing' | 'stopped';

export interface RecordingIndicatorProps {
  status: RecordingStatus;
  /** Длительность записи, секунды. */
  elapsedSeconds: number;
  /** Дополнительная строка (например, «Не отправлено частей: 3»). Без имён пациентов на общих экранах. */
  detail?: string;
  onStop?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  /** compact — в верхней панели; large — поверх экрана блокировки (ПОРТ-22). */
  size?: 'compact' | 'default' | 'large';
  className?: string;
}

/**
 * Статус записи приёма — виден всегда (ТЗ 14.1, принцип 3; СКР-04). Смена статуса
 * объявляется экранным дикторам один раз; таймер не объявляется каждую секунду.
 */
export function RecordingIndicator({ status, elapsedSeconds, detail, onStop, onPause, onResume, size = 'default', className }: RecordingIndicatorProps) {
  const { t } = useTranslation('ui');
  const [announcement, setAnnouncement] = useState('');
  const previous = useRef<RecordingStatus | null>(null);

  useEffect(() => {
    if (previous.current !== status) {
      setAnnouncement(t(`recording.status.${status}`));
      previous.current = status;
    }
  }, [status, t]);

  const dotClass = status === 'paused' ? s.dotPaused : status === 'recording' ? undefined : s.dotMuted;
  const active = status === 'recording' || status === 'paused';

  return (
    <div
      className={cx(
        s.recording,
        size === 'compact' && s.recordingCompact,
        size === 'large' && s.recordingLarge,
        status === 'recording' && s.recordingRecording,
        status === 'offline' && s.recordingOffline,
        className,
      )}
    >
      <span className={cx(s.dot, dotClass)} aria-hidden="true" />
      <span className={s.recordingText}>
        <span className={s.recordingStatus}>{t(`recording.status.${status}`)}</span>
        {detail && size !== 'compact' ? <span className={s.recordingSub}>{detail}</span> : null}
      </span>
      <span className={s.timer}>
        <span className="visually-hidden">{t('recording.elapsed')}: </span>
        <time dateTime={`PT${Math.floor(elapsedSeconds)}S`}>{formatDuration(elapsedSeconds)}</time>
      </span>
      {active ? (
        <span className={s.recordingActions}>
          {status === 'recording' && onPause ? <IconButton label={t('recording.pause')} icon={PauseFilled} size={size === 'compact' ? 'sm' : 'md'} onClick={onPause} /> : null}
          {status === 'paused' && onResume ? <IconButton label={t('recording.resume')} icon={PlayFilledAlt} size={size === 'compact' ? 'sm' : 'md'} onClick={onResume} /> : null}
          {onStop ? (
            <Button variant="danger" size={size === 'compact' ? 'sm' : 'md'} icon={StopFilledAlt} onClick={onStop}>
              {t('recording.stop')}
            </Button>
          ) : null}
        </span>
      ) : null}
      <span className="visually-hidden" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
